/**
 * Transport-agnostic state for the universal conversation shell.
 *
 * The current backend completes a turn in one non-streaming request. Keeping the
 * transport injectable lets a real streaming endpoint replace it later without
 * coupling the presentational shell to SSE or another protocol.
 */

import { useCallback, useState } from 'react';
import {
  aiStudioConversationTransport,
  type ConversationTransport,
} from '@/services/aiStudioService';
import type { ConversationMessage } from '@/types/aiStudio';

export interface UseConversationOptions {
  /** Null while the conversation is still being created or loaded. */
  conversationId: string | null;
  modelConfigurationId?: string | null;
  transport?: ConversationTransport;
  /** Translated user-facing error supplied by the host. */
  sendErrorMessage: string;
  /** Called after a successful turn (e.g. refresh run scorecard after tools). */
  onTurnComplete?: () => void;
}

export interface UseConversationResult {
  messages: ConversationMessage[];
  sending: boolean;
  error: string | null;
  /** Replace the whole list after initial history load or a conversation switch. */
  setMessages: (messages: ConversationMessage[]) => void;
  send: (text: string) => Promise<void>;
  clearError: () => void;
}

/** Client-side ids identify optimistic bubbles until server rows replace them. */
function localId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function useConversation({
  conversationId,
  modelConfigurationId,
  transport = aiStudioConversationTransport,
  sendErrorMessage,
  onTurnComplete,
}: UseConversationOptions): UseConversationResult {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !conversationId || sending) return;

      const now = new Date().toISOString();
      const userId = localId('user');
      const assistantId = localId('assistant');

      setError(null);
      setSending(true);
      setMessages((previous) => [
        ...previous,
        { id: userId, role: 'user', content: trimmed, createdAt: now },
        { id: assistantId, role: 'assistant', content: '', createdAt: now, pending: true },
      ]);

      try {
        const result = await transport.send(conversationId, trimmed, {
          modelConfigurationId,
        });
        setMessages((previous) =>
          previous.flatMap((message) => {
            if (message.id === userId) return [result.userMessage];
            if (message.id === assistantId) return [result.assistantMessage];
            return [message];
          })
        );
        onTurnComplete?.();
      } catch (err) {
        setMessages((previous) => previous.filter((message) => message.id !== assistantId));
        const apiMessage =
          err && typeof err === 'object' && 'response' in err
            ? String(
                (err as { response?: { data?: { error?: string } } }).response?.data?.error || ''
              ).trim()
            : '';
        setError(apiMessage || sendErrorMessage);
        console.error('[ai-studio] conversation send failed', err);
      } finally {
        setSending(false);
      }
    },
    [conversationId, modelConfigurationId, onTurnComplete, sendErrorMessage, sending, transport]
  );

  return { messages, sending, error, setMessages, send, clearError };
}
